/* global $:true */
import debug from "debug";
// import countryList from "./countryList.json";

const MODULE_NAME = "app.core/modules/store-api";
const log = debug(MODULE_NAME);

const tokenEl = document.querySelector('input[name="__RequestVerificationToken"]');
const TOKEN = tokenEl ? tokenEl.value : "";

const apiMetaEl = document.querySelector('meta[name="site.myStoresPageUrl"]');
const API_URL = apiMetaEl ? apiMetaEl.content : "";
if (!API_URL) {
  console.warn("API_URL meta tag not found: meta[name='site.myStoresPageUrl']");
}
if (!TOKEN) {
  console.warn("CSRF token input not found: input[name='__RequestVerificationToken']");
}

window.countryList = [];

const storeListeners = [];
let inflightStoreListPromise = null;
let inflightUserDetailsPromise = null;

function subscribeToStores(callback) {
  storeListeners.push(callback);
}

function notifyStoreListeners(stores) {
  storeListeners.forEach(callback => {
    try {
      callback(stores);
    } catch (err) {
      console.error("Store subscriber error:", err);
    }
  });
}

function getAndSubscribeToStoresApi(callback) {
  getStoreList(true)
    .then(stores => {
        callback(stores);
        subscribeToStores(callback);
    })
    .catch(err => {
      console.error("Error fetching initial store list:", err);
    });
}

function ensureSingleDefault(store) {
    let foundDefault = false;

    store.Addresses.forEach(address => {
        if (address.IsDefault) {
            if (!foundDefault) {
                foundDefault = true;
            } else {
                  address.IsDefault = false;

                  let updateData = {
                        AddressId: address.AddressId,
                        StoreId: store.StoreId,
                        Nickname: address.Nickname,
                        Line0: address.Line0,
                        Line1: address.Line1,
                        DeliveryInstructions: address.DeliveryInstructions,
                        City: address.City,
                        Region: address.Region,
                        PostCode: address.PostCode,
                        CountryCode: address.CountryCode,
                        IsDefault: address.IsDefault
                  };
                  updateAddress(store.StoreId, address.AddressId, updateData);
            }
        }
    });
}


function getStoreList(forceRefresh = false) {
  const cacheKey = "storeList";

  if (!forceRefresh) {
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      log("Returning cached store list from sessionStorage");
      const parsed = JSON.parse(cached);
      notifyStoreListeners(parsed);
      return Promise.resolve(parsed);
    }
    if (inflightStoreListPromise) {
      log("Joining in-flight store request");
      return inflightStoreListPromise;
    }
  }

  inflightStoreListPromise = new Promise((resolve, reject) => {
    fetch(`${API_URL}getstores`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "RequestVerificationToken": TOKEN
      }
    })
    .then(response => {
      if (!response.ok) {
        console.error(`getStoreList. Server error: ${response.status}`);
        return [];
      }
      return response.json();
    })
    .then(stores => {
      stores.forEach(store => {
        store.Addresses.sort((a, b) => {
          if (b.IsDefault === a.IsDefault) {
            return a.Nickname.localeCompare(b.Nickname, undefined, {
              numeric: true,
              sensitivity: 'base'
            });
          }
          return (b.IsDefault === true) - (a.IsDefault === true);
        });
        if (store.Addresses.length) {
          store.Addresses.forEach(address => {
            address.DisplayAddress = `${address.Line0}</br>${address.Line1 ? `${address.Line1}</br>` : ''}${address.City}</br>${address.Region}</br>${address.PostCode}</br>${address.CountryDisplayName}`;
          });
        }
      });

      stores.sort((a, b) => a.Name.toLowerCase().localeCompare(b.Name.toLowerCase()));

      sessionStorage.setItem(cacheKey, JSON.stringify(stores));
      notifyStoreListeners(stores);

      resolve(stores);
    })
    .catch(error => {
      console.error("Error fetching stores:", error);
      reject(error);
    })
    .finally(() => {
      inflightStoreListPromise = null;
    });
  });

  return inflightStoreListPromise;
}


function getUserDetails(callbackFn) {
  if (inflightUserDetailsPromise) {
    return inflightUserDetailsPromise.then(userDetails => {
      if (callbackFn) callbackFn();
      return userDetails;
    });
  }

  inflightUserDetailsPromise = fetch(`${API_URL}Details`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      "RequestVerificationToken": TOKEN
    }
  })
    .then(response => {
      if (!response.ok) {
          console.error(`getUserDetails. Server error: ${response.status}`);
          return {};
      }
      return response.json();
    })
  .then(userDetails => {
    return userDetails;
  })
  .catch(err => {
    console.error("Error fetching user details:", err);
    return Promise.reject(err);
  })
  .finally(() => {
    inflightUserDetailsPromise = null;
  });

  return inflightUserDetailsPromise.then(userDetails => {
    if (callbackFn) callbackFn();
    return userDetails;
  });
}


function addStore(data, callbackFn) {
  return fetch(`${API_URL}createstore`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "RequestVerificationToken": TOKEN
    },
    body: JSON.stringify(data)
  })
  .then(res => {
    if (!res.ok) {
      return res.json()
        .catch(() => ({ message: res.statusText }))
        .then(errorBody => {
          return Promise.reject({
            name: "AddStoreError",
            message: `addStore failed: ${res.status} ${res.statusText}`,
            details: errorBody,
            status: res.status
          });
        });
    }
    return res.json();
  })
  .then(result => {
    if (callbackFn) callbackFn(result);
    return result;
  })
  .catch(err => {
    console.error("addStore error:", err);
    return Promise.reject(err);
  });
}


function updateStore(storeId = "", data, callbackFn) {
  return fetch(`${API_URL}updatestore`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "RequestVerificationToken": TOKEN
    },
    body: JSON.stringify(data)
  })
  .then(res => {
    if (!res.ok) {
      return res.json()
        .catch(() => ({ message: res.statusText }))
        .then(errorBody => {
          return Promise.reject({
            name: "UpdateStoreError",
            message: `updateStore failed: ${res.status} ${res.statusText}`,
            details: errorBody,
            status: res.status
          });
        });
    }
    return res.json();
  })
  .then(result => {
    if (callbackFn) callbackFn(result);
    return result;
  })
  .catch(err => {
    console.error("updateStore error:", err);
    return Promise.reject(err);
  });
}

function deleteStore(storeId = "", callbackFn) {
  return fetch(`${API_URL}deletestore`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "RequestVerificationToken": TOKEN
    },
    body: JSON.stringify({ storeId })
  })
  .then(res => {
    if (!res.ok) {
      throw new Error(`deleteStore failed: ${res.status} ${res.statusText}`);
    }
    return res.json();
  })
  .then(result => {
    if (callbackFn) callbackFn(result);
    return result;
  })
  .catch(err => {
    console.error("deleteStore error:", err);
    return Promise.reject(err);
  });
}

function addAddress(storeId = "", data, callbackFn) {
  return fetch(`${API_URL}createaddress`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "RequestVerificationToken": TOKEN
    },
    body: JSON.stringify(data)
  })
  .then(res => {
    if (!res.ok) {
      return res.json()
        .catch(() => ({ message: res.statusText }))
        .then(errorBody => {
          const error = {
            name: "AddAddressError",
            message: `addAddress failed: ${res.status} ${res.statusText}`,
            details: errorBody,
            status: res.status
          };
          return Promise.reject(error);
        });
    }
    return res.json();
  })
  .then(result => {
    if (callbackFn) callbackFn(result);
    return result;
  })
  .catch(err => {
    console.error("addAddress error:", err);
    return Promise.reject(err);
  });
}

function updateAddress(storeId = "", addressId, data, callbackFn) {
  return fetch(`${API_URL}updateaddress`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "RequestVerificationToken": TOKEN
    },
    body: JSON.stringify(data)
  })
  .then(res => {
    if (!res.ok) {
      return res.json()
        .catch(() => ({ message: res.statusText }))
        .then(errorBody => {
          return Promise.reject({
            name: "UpdateAddressError",
            message: `updateAddress failed: ${res.status} ${res.statusText}`,
            details: errorBody,
            status: res.status
          });
        });
    }
    return res.json();
  })
  .then(result => {
    if (callbackFn) callbackFn(result);
    return result;
  })
  .catch(err => {
    console.error("updateAddress error:", err);
    return Promise.reject(err);
  });
}

function deleteAddress(storeId = "", addressId, callbackFn) {
  return fetch(`${API_URL}deleteaddress`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "RequestVerificationToken": TOKEN
    },
    body: JSON.stringify({ storeId, addressId })
  })
  .then(res => {
    if (!res.ok) {
      throw new Error(`deleteAddress failed: ${res.status} ${res.statusText}`);
    }
    return res.json();
  })
  .then(result => {
    if (callbackFn) callbackFn(result);
    return result;
  })
  .catch(err => {
    console.error("deleteAddress error:", err);
    return Promise.reject(err);
  });
}

function setAsDefaultAddress(storeId = "", addressId, callbackFn) {
  return fetch(`${API_URL}setdefaultaddress`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "RequestVerificationToken": TOKEN
    },
    body: JSON.stringify({ storeId, addressId })
  })
  .then(res => {
    if (!res.ok) {
      throw new Error(`setAsDefaultAddress failed: ${res.status} ${res.statusText}`);
    }
    return res.json();
  })
  .then(result => {
    if (callbackFn) callbackFn(result);
    return result;
  })
  .catch(err => {
    console.error("setAsDefaultAddress error:", err);
    return Promise.reject(err);
  });
}

function setCurrentStore(storeId = "", addressId, callbackFn) {
  const reqBody = { storeId };
  if (addressId != undefined) {
    reqBody.addressId = addressId;
  }
  return fetch(`${API_URL}setcurrentstore`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "RequestVerificationToken": TOKEN
    },
    body: JSON.stringify(reqBody)
  })
  .then(res => {
    if (!res.ok) {
      throw new Error(`setCurrentStore failed: ${res.status} ${res.statusText}`);
    }
    return res.json();
  })
  .then(result => {
    if (callbackFn) callbackFn(result);
    return result;
  })
  .catch(err => {
    console.error("setCurrentStore error:", err);
    return Promise.reject(err);
  });
}

function getUberCuisines(callbackFn) {
  const cacheKey = "cuisineCategoryList";

  const cached = sessionStorage.getItem(cacheKey);
  if (cached) {
    const parsed = JSON.parse(cached);
    if (callbackFn) callbackFn(parsed);
    return Promise.resolve(parsed);
  }

  return fetch(`${API_URL}GetCuisineCategories`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      "RequestVerificationToken": TOKEN
    }
  })
  .then(res => {
    if (!res.ok) throw new Error("Failed to fetch cuisine categories");
    return res.json();
  })
  .then(cuisineCategoryList => {
    sessionStorage.setItem(cacheKey, JSON.stringify(cuisineCategoryList));
    if (callbackFn) callbackFn(cuisineCategoryList);
    return cuisineCategoryList;
  });
}

function getCountriesList(callbackFn) {
  const cacheKey = "countryList";

  const cached = sessionStorage.getItem(cacheKey);
  if (cached) {
    const parsed = JSON.parse(cached);
    window.countryList = parsed;
    if (callbackFn) callbackFn(parsed);
    return Promise.resolve(parsed);
  }

  return fetch(`${API_URL}GetCountries`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      "RequestVerificationToken": TOKEN
    }
  })
  .then(res => {
    if (!res.ok) throw new Error("Failed to fetch countryList");
    return res.json();
  })
  .then(countryList => {
    window.countryList = countryList;
    sessionStorage.setItem(cacheKey, JSON.stringify(countryList));
    if (callbackFn) callbackFn(countryList);
    return countryList;
  });
}

window.storeApi = {
  addStore,
  updateStore,
  deleteStore,
  addAddress,
  updateAddress,
  deleteAddress,
  setAsDefaultAddress,
  setCurrentStore,
  getStoreList,
  subscribeToStores,
  getAndSubscribeToStoresApi,
  getUserDetails,
  getUberCuisines,
  getCountriesList,
};


// WEBPACK FOOTER //
// ./client/src/core/modules/store-api/index.js